import React, { useEffect, useState } from "react";
import { Box, Avatar, Typography, Tabs, Tab, SxProps } from "@mui/material";
import {
  ChatBubbleOutline,
  ShortcutOutlined,
  GroupAddOutlined,
  CalendarTodayOutlined,
  PeopleAltOutlined,
} from "@mui/icons-material";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import StatCard from "./StatCard";
import { useNavigate, useParams } from "react-router-dom";
import MessageStatsTable from "./MessageStatsTable";
import DistributionAnalyticsTable from "./DistributionAnalyticsTable";
import MandatoryMessagesTable from "./MandatoryMessagesTable";
import { getUserAnalytics, getUserById } from "../api/requests/users";
import DatesDropdown from "./DatesDropdown";
import dayjs from "dayjs";
import { DateRangeType, mainContentStyles } from "./Dashboard";
import MandatoryRepliesTable from "./MandatoryReplies";
import { convertISTtoUTC } from "../utils/utils";
import { colors } from "../utils/constants";
import {
  titleDescriptionStyles,
  titleStyles,
} from "../components/CommDataGrid";
import {
  breadcrumbActiveStyles,
  breadcrumbIntitalStyles,
} from "./mandatoryMessageDashboard";

interface UserAnalyticsStatsType {
  eventMemberships: number;
  eventsCreated: number;
  groupMemberships: number;
  groupsCreated: number;
  totalForwards: number;
  totalMessages: number;
  totalShares: number;
}

interface UserType {
  dateOfBirth: string | null;
  email: string;
  fullName: string;
  location: string | null;
  mobileNo: string | null;
  policeStation: string | null;
  rank: string | null;
  rankId: number | null;
  role: string | null;
  userId: number | null;
  zone: string | null;
}

const UserAnalytics: React.FC = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [userAnalyticsStats, setUserAnalyticsStats] =
    useState<UserAnalyticsStatsType>({
      eventMemberships: 0,
      eventsCreated: 0,
      groupMemberships: 0,
      groupsCreated: 0,
      totalForwards: 0,
      totalMessages: 0,
      totalShares: 0,
    });
  // Initialize default dates for "This Month" in UTC
  const [selectedDates, setSelectedDates] = useState<DateRangeType>({
    fromDate: convertISTtoUTC(dayjs().startOf("month").startOf("day")),
    toDate: convertISTtoUTC(dayjs().endOf("month").endOf("day")),
  });
  const [userDetails, setUserDetails] = useState<UserType>({
    dateOfBirth: null,
    email: "",
    fullName: "",
    location: null,
    mobileNo: null,
    policeStation: null,
    rank: null,
    rankId: null,
    role: null,
    userId: null,
    zone: null,
  });

  const stats = [
    {
      label: "Total Messages",
      value: userAnalyticsStats.totalMessages,
      icon: <ChatBubbleOutline />,
    },
    {
      label: "Total Forwards",
      value: userAnalyticsStats.totalForwards,
      icon: <ShortcutOutlined />,
    },
    {
      label: "Groups Created",
      value: userAnalyticsStats.groupsCreated,
      icon: <GroupAddOutlined />,
    },
    {
      label: "Events Created",
      value: userAnalyticsStats.eventsCreated,
      icon: <CalendarTodayOutlined />,
    },
    {
      label: "Group Memberships",
      value: userAnalyticsStats.groupMemberships,
      icon: <PeopleAltOutlined />,
    },
    {
      label: "Event Memberships",
      value: userAnalyticsStats.eventMemberships,
      icon: <CalendarTodayOutlined />,
    },
  ];

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const fetchUserAnalytics = async () => {
    const response =
      userId &&
      (await getUserAnalytics({
        userId,
        fromDate: selectedDates.fromDate,
        toDate: selectedDates.toDate,
      }));
    if (response) {
      setUserAnalyticsStats(response);
    }
  };

  const handleDateChange = (fromDate: string, toDate: string) => {
    setSelectedDates({ fromDate, toDate });
  };

  const fetchUserDetails = async () => {
    try {
      const response = userId && (await getUserById(Number(userId)));
      if (response) setUserDetails(response);
    } catch (error) {
      console.error("Error while fetching the user datails", error);
    }
  };

  useEffect(() => {
    fetchUserAnalytics();
  }, [selectedDates]);

  useEffect(() => {
    fetchUserDetails();
  }, []);

  return (
    <>
      {/* Sticky Header Section */}
      <Box sx={mainContentStyles}>
        {/* Breadcrumb */}
        <Box sx={breadcrumbStyles}>
          <Typography onClick={() => navigate(-1)} sx={breadcrumbIntitalStyles}>
            User Management
          </Typography>
          <Typography sx={breadcrumbActiveStyles}>/ User Analytics</Typography>
        </Box>

        {/* Title Section */}
        <Box sx={titleSectionStyles}>
          <Box>
            <Typography sx={titleStyles}>User Profile</Typography>
            <Typography sx={titleDescriptionStyles}>
              Create a custom role with specific permissions and
              responsibilities.
            </Typography>
          </Box>
          <DatesDropdown onDateChange={handleDateChange} />
        </Box>
      </Box>

      {/* Scrollable Content Section */}
      <Box
        sx={{
          height: "calc(100vh - 170px)",
          overflow: "auto",
          px: 3,
        }}
      >
        {/* User Profile Section */}
        <Box sx={userProfileSx}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Avatar sx={{ width: 42, height: 42, background: "#F0F0F0" }}>
              <PersonOutlineOutlinedIcon
                sx={{ width: 24, height: 24, color: "#000000" }}
              />
            </Avatar>
            <Box>
              <Typography fontWeight={400} fontSize="16px">
                {userDetails.fullName}
              </Typography>
              <Typography sx={{ color: "#a5a8a6", fontSize: "14px" }}>
                {userDetails.rank}
              </Typography>
            </Box>
          </Box>

          {/* Stats Cards */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
              },
              gap: 2,
            }}
          >
            {stats?.map((stat, idx) => (
              <StatCard
                label={stat.label}
                value={stat.value}
                icon={stat.icon}
                key={idx}
              />
            ))}
          </Box>
        </Box>

        {/* Tabs and Tables */}
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          sx={{ ...tabsSx, gap: 3, mt: 3 }}
          TabIndicatorProps={{
            style: { display: "none" },
          }}
        >
          <Tab label="Message Activity" sx={{ ...tabSx, ml: 0 }} />
          <Tab label="Message Forwards" sx={tabSx} />
          <Tab label="Mandatory Messages" sx={tabSx} />
          <Tab label="Mandatory Replies" sx={tabSx} />
        </Tabs>
        {tabIndex === 0 && <MessageStatsTable />}
        {tabIndex === 1 && <DistributionAnalyticsTable />}
        {tabIndex === 2 && <MandatoryMessagesTable />}
        {tabIndex === 3 && <MandatoryRepliesTable />}
      </Box>
    </>
  );
};

export default UserAnalytics;

export const tabSx: SxProps = {
  color: "#878787",
  backgroundColor: colors.white,
  textTransform: "capitalize",
  fontWeight: 500,
  px: 4,
  py: 1,
  borderRadius: "68px",
  ml: 1,
  height: "42px",
  "&.Mui-selected": {
    color: "#020817",
    background: "#ffffff",
    minHeight: "auto",
  },
};

export const tabsSx: SxProps = {
  mt: 2,
  width: "fit-content",
  borderRadius: "6px",
  display: "flex",
  alignItems: "center",
};

const userProfileSx: SxProps = {
  display: "flex",
  flexDirection: "column",
  gap: 1,
  borderRadius: "10px",
  p: 2,
  backgroundColor: colors.white,
};

export const sectionTitleSx: SxProps = {
  color: "#020817",
  fontWeight: 500,
  fontSize: "20px",
};

export const sectionDescriptionSx: SxProps = {
  color: "#647488",
  fontWeight: 400,
  fontSize: "14px",
  paddingBottom: 2,
};

const breadcrumbStyles: SxProps = {
  display: "flex",
  gap: 1,
  alignItems: "center",
  pl: 3,
  pt: 1,
};

const titleSectionStyles: SxProps = {
  display: "flex",
  justifyContent: "space-between",
  px: 3,
  pt: 1,
};
